<template>
  <div
    class="nft-currency-selector"
    :class="{ isFocused: isFocused }"
  > 
    <div class="nft-currency-selector__container">
      <CurrencyAutocompleteSelect
        :name="props.name"
        :defaultValue="defaultValue[`${name}Type`]"
        :type="'text'"
        :items="items"
        :isLoading="isLoading"
        :isDisabled="autocompleteIsDisabled"
        autocompleteType="currency"
        class="dropdown-select"
        @updateAutocomplete="updateSelectedSingle"
      >
        <template #selectItem="{ item }">
          <span
            :class="`currency-flag currency-flag-${item.toLowerCase()}`"
          ></span>
          <span class="select-list__text">{{ item }}</span>
        </template>
      </CurrencyAutocompleteSelect>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, computed, inject } from "vue";

import { useNFTOrderFormStore } from "@/stores/forms/nftOrderFormStore";
import { toCurrencyFormat } from "@/composables/helpers/useStringHelpers";

import CryptoIcon from "@/components/icons/CryptoIcon.vue";
import CurrencyAutocompleteSelect from "../selects/CurrencyAutocompleteSelect.vue";
import SkeleLoader from "@/components/ui/loaders/SkeleLoader.vue";

const emit = defineEmits(["dropdownValueUpdate"]);
const props = defineProps({
  items: { type: Array, required: true },
  name: { type: String, default: "currency-input" },
  label: { type: String, default: "Default label" },
  defaultValue: { type: [Object, String], required: true },
  placeholder: { type: String, required: true },
  type: { type: String, required: true },
  errors: { type: String, default: "" },
  isLoading: { type: Boolean, default: true },
});
const nftOrderStore = useNFTOrderFormStore();
const selectedSingle = ref("");
const isFocused = ref(false);
const isAmountLoading = ref(true);

const fiatAmount = computed(() => parseFloat(nftOrderStore.form.fiatAmount.value).toFixed(2));
const fiatType = computed(() => nftOrderStore.form.fiatType.value);
const feeAmount = computed(() => {
  const fees = parseFloat(nftOrderStore.feeData.networkFee) + parseFloat(nftOrderStore.feeData.gatewayFee);
  return fees.toFixed(2);
})

const updateSelectedSingle = (selectedItem) => {
  isAmountLoading.value = true;
  selectedSingle.value = selectedItem;
  emit("dropdownValueUpdate", {
    [`${props.name}Type`]: selectedItem,
  });
};

watch(
  () =>
    props.items &&
    props.defaultValue &&
    props.defaultValue[`${props.name}Type`],
  () => {
    if (props.defaultValue[`${props.name}Type`])
      selectedSingle.value = props.defaultValue[`${props.name}Type`];
  },
  { deep: true }
);

const disableByInputName = inject("isDisabledByKey");
const autocompleteIsDisabled = computed(() => {
  return (
    Object.getOwnPropertyDescriptor(disableByInputName.value, `${props.name}Type`) &&
    disableByInputName.value[`${props.name}Type`]
  );
});

watch(() => nftOrderStore.form.fiatAmount.value, () => {
  if (!isNaN(parseFloat(nftOrderStore.form.fiatAmount.value))){
    isAmountLoading.value = false
  } else {
    isAmountLoading.value = true
  }
})

</script>

<style lang="scss" scoped>
$flag-dimension: 32px;
.nft-currency-selector {
  border-radius: 1.75rem;
  margin-bottom: calc($formFieldSpacing/2);
  position: relative;
  margin-top: 1px; // To accomodate the outline for the focus.

  @media screen and (max-width: $screen-sm) {
    padding-top: 0;
    padding-bottom: calc($padding-std/2);
  }

  &__container {
    display: grid;
    grid-template-rows: 0.8fr;
    grid-template-columns: 3fr 0.5fr;
    align-items: center;
    position: relative;
  }

  &__label {
    font-size: $font-body; //1.2rem;
    margin-left: 1.6rem;

    @media screen and (max-width: $screen-sm) {
      margin-left: 1rem;
    }
  }

  &.isFocused {
    outline: 1px solid #0072ce;
  }
  &.hasError {
    outline: 1px solid var(--error-color);
  }

  &__amount {
    grid-row-start: 1;
    grid-row-end: span 2;
    grid-column-start: 2;
    text-align: right;
    align-self: end;
  }

  &__fiat-amount {
    font-size: $font-body * 0.95;
    margin-bottom: 0.8rem;
    padding-right: calc($padding-std / 2);
  }

  &__fee {
    grid-row-start: 2;
    padding-right: calc($padding-std / 2);

  }

  .skeleton {
    grid-row-start: 1;
    grid-row-end: span 2;
    grid-column-start: 2;
  }
}

.nft-currency-selector:first-child {
  margin-top: 0.5em;
}

.dropdown-select {
  grid-column-start: 2;
  background: var(--input-background);
  padding: 0 0.5em 0 0.8em;
  border-radius: $border-radius;

  &__selected-item {
    display: flex;
    align-items: center;

    &__icon {
      margin-inline: 0.75rem;

      &.fiat {
        width: 25px;
        height: 25px;
        border-radius: 50%;
      }

      &.coin {
        width: 25px;
      }
    }
  }
  .crypto-icon {
    display: flex;
    min-width: 25px;
  }
}

.select-list__text {
  padding-left: 0.75rem;
}
</style>
